import { cn } from '@/lib/cn';
import { type ReactNode } from 'react';

type IndentRailProps = {
  /**
   * If provided, the rail conditionally reveals/hides its children.
   * If omitted, the rail is always visible (static rail) and the
   * consumer controls any vertical spacing via `className`.
   */
  expanded?: boolean;
  /**
   * Vertical spacing between the trigger above and the revealed content
   * when `expanded` is true. Ignored when `expanded` is undefined.
   *
   * @default 'md' (mt-3)
   */
  spacing?: 'sm' | 'md';
  /** Classes appended to the rail wrapper. */
  className?: string;
  /** Test id forwarded to the rail wrapper for stable selection in tests. */
  'data-testid'?: string;
  children: ReactNode;
};

/**
 * Settings UI primitive: a vertical "rail" — a logical-start border with
 * indented content (so it sits on the left in LTR and the right in RTL).
 * Can be used statically (always visible) or conditionally (reveals/hides
 * via the `expanded` prop).
 *
 * When conditional, the toggle is intentionally instant (no animation) —
 * both opacity and height transitions for variable-height content tend
 * to look choppy or produce mismatched motion, so the rail simply snaps
 * in and out. `invisible` keeps the collapsed content out of the
 * accessibility tree.
 *
 * Layout of the children themselves is left to the caller — pass any
 * additional `flex`/`gap-*`/etc. via `className` when needed.
 */
export const IndentRail = ({
  expanded,
  spacing = 'md',
  className,
  'data-testid': dataTestId,
  children,
}: IndentRailProps) => {
  const conditionalClasses =
    expanded === undefined
      ? null
      : expanded
        ? cn('h-auto', spacing === 'sm' ? 'mt-2' : 'mt-3')
        : 'invisible mt-0 h-0 overflow-hidden';

  return (
    <div
      className={cn('border-s border-border ps-4', conditionalClasses, className)}
      data-testid={dataTestId}
      data-state={
        expanded === undefined ? undefined : expanded ? 'expanded' : 'collapsed'
      }
    >
      {children}
    </div>
  );
};
